import type {
  AppRuntime,
  SafeAreaInsets,
  TelegramColorScheme,
  TelegramEvent,
  TelegramMainButton,
  TelegramThemeParams,
  TelegramWebApp,
  TelegramWindow,
} from './telegramTypes.js';

const EMPTY_SAFE_AREA: SafeAreaInsets = { top: 0, right: 0, bottom: 0, left: 0 };
const MAX_SAFE_AREA_PX = 1_000;

function normalizeDimension(value: number | undefined, maximum: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return 0;
  return Math.min(value, maximum);
}

function normalizeSafeArea(value: SafeAreaInsets | undefined): SafeAreaInsets {
  if (!value) return EMPTY_SAFE_AREA;
  return {
    top: normalizeDimension(value.top, MAX_SAFE_AREA_PX),
    right: normalizeDimension(value.right, MAX_SAFE_AREA_PX),
    bottom: normalizeDimension(value.bottom, MAX_SAFE_AREA_PX),
    left: normalizeDimension(value.left, MAX_SAFE_AREA_PX),
  };
}

export class TelegramAdapter {
  readonly #webApp: TelegramWebApp | null;

  constructor(targetWindow?: TelegramWindow) {
    this.#webApp = targetWindow?.Telegram?.WebApp ?? null;
  }

  getRuntime(): AppRuntime {
    return this.#webApp ? 'TELEGRAM_MINI_APP' : 'WEB';
  }

  isTelegram(): boolean {
    return this.#webApp !== null;
  }

  getColorScheme(): TelegramColorScheme {
    return this.#webApp?.colorScheme === 'light' ? 'light' : 'dark';
  }

  getThemeParams(): TelegramThemeParams {
    return this.#webApp?.themeParams ?? {};
  }

  getSafeArea(): SafeAreaInsets {
    return normalizeSafeArea(this.#webApp?.safeAreaInset);
  }

  getContentSafeArea(): SafeAreaInsets {
    return normalizeSafeArea(this.#webApp?.contentSafeAreaInset);
  }

  getViewportHeight(): number | null {
    const height = this.#webApp?.viewportHeight;
    return typeof height === 'number' && Number.isFinite(height) && height > 0 ? height : null;
  }

  /** Raw initData is untrusted until it is cryptographically verified by the backend. */
  getUnverifiedInitData(): string | null {
    const initData: unknown = this.#webApp?.initData;
    return typeof initData === 'string' && initData.length > 0 ? initData : null;
  }

  ready(): void {
    this.#webApp?.ready();
  }

  expand(): void {
    this.#webApp?.expand();
  }

  close(): void {
    this.#webApp?.close();
  }

  showBackButton(callback: () => void): () => void {
    const button = this.#webApp?.BackButton;
    if (!button) return () => undefined;
    button.onClick(callback);
    button.show();
    return () => {
      button.offClick(callback);
      button.hide();
    };
  }

  getMainButton(): TelegramMainButton | null {
    return this.#webApp?.MainButton ?? null;
  }

  subscribe(event: TelegramEvent, callback: () => void): () => void {
    if (!this.#webApp) return () => undefined;
    this.#webApp.onEvent(event, callback);
    return () => this.#webApp?.offEvent(event, callback);
  }
}

export function createTelegramAdapter(targetWindow?: TelegramWindow): TelegramAdapter {
  const resolvedWindow =
    targetWindow ?? (typeof window === 'undefined' ? undefined : (window as TelegramWindow));
  return new TelegramAdapter(resolvedWindow);
}
